
import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSettings } from '@/context/SettingsContext';

export type TutorialStep = {
  id: string;
  title: string;
  content: string;
  targetSelector?: string;
  page: '/dashboard' | '/journal';
  action?: 'click' | 'type' | 'observe';
  nextCondition?: () => boolean;
  skipable?: boolean;
};

const TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Unstuck!',
    content: 'Let\'s create your first journal entry together. This will help you understand how the dice-driven journaling system works.',
    page: '/dashboard',
    skipable: true
  },
  {
    id: 'click-create-entry',
    title: 'Start Your First Entry',
    content: 'Click this button to create your first journal entry. This will take you to the journaling interface.',
    targetSelector: '[data-tutorial="create-entry-button"]',
    page: '/dashboard',
    action: 'click'
  },
  {
    id: 'describe-stuck-point',
    title: 'Describe What You\'re Stuck On',
    content: 'Start by describing any challenge or situation where you feel blocked. This could be anything from a work decision to a personal dilemma.',
    targetSelector: '[data-tutorial="stuck-point-field"]',
    page: '/journal',
    action: 'type'
  },
  {
    id: 'select-difficulty',
    title: 'Choose Your Challenge Level',
    content: 'Select how significant this challenge feels to you. This helps the system provide appropriate guidance.',
    targetSelector: '[data-tutorial="difficulty-selector"]',
    page: '/journal',
    action: 'click'
  },
  {
    id: 'roll-dice',
    title: 'Roll the Dice for Guidance',
    content: 'Click "Roll Dice" to get your personalized prompts. Each number corresponds to a different type of awareness.',
    targetSelector: '[data-tutorial="roll-dice-button"]',
    page: '/journal',
    action: 'click'
  },
  {
    id: 'perception-response',
    title: 'Answer the Perception Prompt',
    content: 'This prompt helps you notice what\'s happening right now. Take your time to reflect honestly.',
    targetSelector: '[data-tutorial="perception-field"]',
    page: '/journal',
    action: 'type'
  },
  {
    id: 'continue-intention',
    title: 'Continue to Intention',
    content: 'Click "Continue" to move to the next stage where you\'ll explore your deeper intentions.',
    targetSelector: '[data-tutorial="continue-button"]',
    page: '/journal',
    action: 'click'
  },
  {
    id: 'intention-response',
    title: 'Explore Your Intention',
    content: 'This prompt helps you connect with what you really want to achieve or how you want to feel.',
    targetSelector: '[data-tutorial="intention-field"]',
    page: '/journal',
    action: 'type'
  },
  {
    id: 'continue-next-step',
    title: 'Continue to Next Step',
    content: 'Click "Continue" to move to the final stage where you\'ll identify a concrete action.',
    targetSelector: '[data-tutorial="continue-button"]',
    page: '/journal',
    action: 'click'
  },
  {
    id: 'next-step-response',
    title: 'Identify Your Next Step',
    content: 'Think of one small, concrete action you can take. It doesn\'t have to solve everything - just move you forward.',
    targetSelector: '[data-tutorial="next-step-field"]',
    page: '/journal',
    action: 'type'
  },
  {
    id: 'save-entry',
    title: 'Save Your Entry',
    content: 'Great work! Click "Save Entry" to complete your first journal session. You can always come back to edit or create new entries.',
    targetSelector: '[data-tutorial="save-button"]',
    page: '/journal',
    action: 'click'
  },
  {
    id: 'tutorial-complete',
    title: 'Tutorial Complete!',
    content: 'Congratulations! You\'ve completed your first journal entry. You can now use Unstuck whenever you feel stuck or need clarity.',
    page: '/journal',
    skipable: true
  }
];

export const useInteractiveTutorial = () => {
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const highlightElementRef = useRef<HTMLElement | null>(null);
  const { setTutorialCompleted, saveSettings } = useSettings();

  const currentStep = TUTORIAL_STEPS[currentStepIndex];
  const isLastStep = currentStepIndex >= TUTORIAL_STEPS.length - 1;

  // Load tutorial state from localStorage
  useEffect(() => {
    const savedState = localStorage.getItem('unstuck_tutorial_state');
    if (savedState) {
      const { stepIndex, completed } = JSON.parse(savedState);
      setCurrentStepIndex(stepIndex);
      setCompletedSteps(completed);
    }
  }, []);

  // Save tutorial state to localStorage
  const saveTutorialState = useCallback((stepIndex: number, completed: string[]) => {
    localStorage.setItem('unstuck_tutorial_state', JSON.stringify({
      stepIndex,
      completed
    }));
  }, []);

  // Start the tutorial
  const startTutorial = useCallback(() => {
    setIsActive(true);
    setCurrentStepIndex(0);
    setCompletedSteps([]);
    saveTutorialState(0, []);
    
    // Navigate to dashboard if not already there
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard');
    }
  }, [navigate, location.pathname, saveTutorialState]);

  // Exit tutorial
  const exitTutorial = useCallback(() => {
    setIsActive(false);
    localStorage.removeItem('unstuck_tutorial_state');
    if (highlightElementRef.current) {
      highlightElementRef.current.style.outline = '';
      highlightElementRef.current.style.outlineOffset = '';
    }
  }, []);

  // Complete tutorial
  const completeTutorial = useCallback(async () => {
    setIsActive(false);
    
    // Save to settings context (database + localStorage)
    setTutorialCompleted(true);
    await saveSettings();
    
    // Clean up tutorial state
    localStorage.removeItem('unstuck_tutorial_state');
    
    if (highlightElementRef.current) {
      highlightElementRef.current.style.outline = '';
      highlightElementRef.current.style.outlineOffset = '';
    }
  }, [setTutorialCompleted, saveSettings]);

  // Go to next step
  const nextStep = useCallback(() => {
    if (isLastStep) {
      completeTutorial();
      return;
    }

    const nextStepIndex = currentStepIndex + 1;
    const nextStep = TUTORIAL_STEPS[nextStepIndex];
    
    setCurrentStepIndex(nextStepIndex);
    setCompletedSteps(prev => [...prev, currentStep.id]);
    saveTutorialState(nextStepIndex, [...completedSteps, currentStep.id]);

    // Navigate to the next step's page if different
    if (nextStep.page !== location.pathname) {
      navigate(nextStep.page);
    }
  }, [currentStepIndex, currentStep, isLastStep, completeTutorial, navigate, location.pathname, completedSteps, saveTutorialState]);

  // Go to previous step
  const previousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      const prevStepIndex = currentStepIndex - 1;
      const prevStep = TUTORIAL_STEPS[prevStepIndex];
      
      setCurrentStepIndex(prevStepIndex);
      saveTutorialState(prevStepIndex, completedSteps.slice(0, -1));

      // Navigate to the previous step's page if different
      if (prevStep.page !== location.pathname) {
        navigate(prevStep.page);
      }
    }
  }, [currentStepIndex, navigate, location.pathname, completedSteps, saveTutorialState]);

  // Skip current step
  const skipStep = useCallback(() => {
    if (currentStep.skipable) {
      nextStep();
    }
  }, [currentStep, nextStep]);

  // Highlight target element
  const highlightElement = useCallback((selector: string) => {
    // Remove previous highlight
    if (highlightElementRef.current) {
      highlightElementRef.current.style.outline = '';
      highlightElementRef.current.style.outlineOffset = '';
    }

    // Add new highlight
    const element = document.querySelector(selector) as HTMLElement;
    if (element) {
      element.style.outline = '3px solid #6127A0';
      element.style.outlineOffset = '2px';
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      highlightElementRef.current = element;
    }
  }, []);

  // Check if current step should be shown on current page
  const shouldShowStep = currentStep && currentStep.page === location.pathname;

  return {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps: TUTORIAL_STEPS.length,
    isLastStep,
    shouldShowStep,
    completedSteps,
    startTutorial,
    exitTutorial,
    nextStep,
    previousStep,
    skipStep,
    highlightElement
  };
};
